/** @file Function to create control component that renders a <input> or a <textarea>. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as constants from '../constants';
import { injectFieldContext, useFieldContextValue } from '../context';
const commonProps = {
    /**
     * The value of the input element.
     *
     * Dev Note: This prop should be optional, so TypeScript won't complain about missing `value` when using v-model.
     */
    value: p(String).optional,
    /** This prop controls the typography setting of the input. */
    textStyle: p.ofType().default('body-medium'),
    /** The read-only attribute of the <input> element. The input will behave as a read-only field if this prop is true. */
    readOnly: p(Boolean).default(false),
    /** The disabled attribute of the <input> element. */
    disabled: p(Boolean).default(false),
    /** Optional native on to be added to the <input> element. */
    nativeOn: p.ofType().optional,
};
/** Control component rendering a <input> element. */
export const Input = tsx.withHtmlAttrs(tsx.componentFactoryOf().create({
    name: `${constants.nameControl}-input`,
    inject: injectFieldContext(),
    props: {
        ...commonProps,
        type: p(String).default('text'),
    },
    computed: useFieldContextValue(),
    /** @returns Node of the component. */
    render() {
        return (<input class={[constants.nameControl, this.textStyle, { 'read-only': this.readOnly }]} id={this.fieldContext.inputId} type={this.type} aria-describedby={this.fieldContext.descriptionId} readonly={this.readOnly} disabled={this.disabled} aria-disabled={this.disabled} domProps={{
                value: this.value, // value must be set under domProps.
            }} nativeOn={this.nativeOn} onInput={(evt) => {
                tsx.emitOn(this, 'onValidity', evt.target.validity);
                tsx.emitOn(this, 'onInput', evt.target.value);
            }}/>);
    },
}));
/** Control component rendering a <textarea> element. */
export const Textarea = tsx.withHtmlAttrs(tsx.componentFactoryOf().create({
    name: `${constants.nameControl}-textarea`,
    inject: injectFieldContext(),
    props: commonProps,
    computed: useFieldContextValue(),
    /** @returns Node of the component. */
    render() {
        return (<textarea class={[constants.nameControl, this.textStyle, { 'read-only': this.readOnly }]} id={this.fieldContext.inputId} aria-describedby={this.fieldContext.descriptionId} readonly={this.readOnly} disabled={this.disabled} aria-disabled={this.disabled} domProps={{
                value: this.value, // value must be set under domProps.
            }} nativeOn={this.nativeOn} onInput={(evt) => {
                tsx.emitOn(this, 'onInput', evt.target.value);
            }}/>);
    },
}));
